import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API_ROOT } from "@/config";
import SEO from "@/components/seo";
import { Check, X, Loader2, ArrowRight } from "lucide-react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import React from "react";

const PALETTE = {
  inchworm: "#B1FA63",
  gunmetal: "#243837",
  orange: "#FE7733",
  paleViolet: "#B2A1FF",
  brightSnow: "#FFFFFF",
  americanSilver: "#D1D1D1",
};

const getScoreColor = (score: number) => {
  if (score >= 90) return PALETTE.inchworm;
  if (score >= 61) return "#fbbf24";
  return "#f43f5e";
};

const FreeCheckResult = () => {
  const { check_id } = useParams<{ check_id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<any>(null);

  useEffect(() => {
    if (!check_id) return;

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await fetch(`${API_ROOT}/free-check-result/${check_id}`);
        if (!res.ok) throw new Error("Results not found");
        const json = await res.json();

        if (cancelled) return;

        if (json.status === "completed") {
          setData(json);
          setIsLoading(false);
        } else if (json.status === "failed") {
          setError(json.error || "Analysis failed. The video may be private or unavailable.");
          setIsLoading(false);
        } else {
          attempts++;
          if (attempts > 120) {
            setError("Analysis is taking longer than expected. Please check back later.");
            setIsLoading(false);
          } else {
            setTimeout(poll, 3000);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || "Failed to load results.");
          setIsLoading(false);
        }
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [check_id]);

  const extractText = (item: any): string => {
    let text = typeof item === "string" ? item : item.input || item.requirement || item.element || item.text || "";
    if (text.startsWith("Element: ")) text = text.substring(9);
    else if (text.startsWith("Element:")) text = text.substring(8);
    return text.trim();
  };

  const requirements = useMemo(() => {
    if (!data) return [];
    const rows: any[] = [];
    let n = 1;

    (data.non_compliant || []).forEach((item: any) => {
      rows.push({
        requirementNumber: n++,
        requirement: extractText(item),
        compliance: "❌",
        ai_answer: typeof item === "object" ? item.ai_answer : undefined,
      });
    });

    (data.compliant || []).forEach((item: any) => {
      rows.push({
        requirementNumber: n++,
        requirement: extractText(item),
        compliance: "✅",
        ai_answer: typeof item === "object" ? item.ai_answer : undefined,
      });
    });

    return rows;
  }, [data]);

  const score = data?.compliance_score ?? 0;
  const scoreColor = getScoreColor(score);

  const columnDefs: ColDef[] = [
    {
      headerName: "Alignment",
      field: "compliance",
      width: 120,
      resizable: false,
      filter: false,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
      cellRenderer: (params: ICellRendererParams) => {
        const ok = params.value === "✅";
        return (
          <div className="flex items-center justify-center h-full w-full" style={{ margin: "0 auto" }}>
            <div
              className="flex items-center justify-center rounded-full"
              style={{ width: 24, height: 24, backgroundColor: ok ? PALETTE.inchworm : "#f43f5e" }}
            >
              {ok ? <Check className="w-4 h-4" style={{ color: "#fff" }} strokeWidth={3} /> : <X className="w-4 h-4" style={{ color: "#fff" }} strokeWidth={3} />}
            </div>
          </div>
        );
      },
      tooltipValueGetter: (params: any) => {
        const ai = params.data?.ai_answer;
        if (!ai) return null;
        return `${params.data?.compliance === "✅" ? "Compliant" : "Non-Compliant"}: ${ai}`;
      },
    },
    {
      headerName: "Requirement",
      field: "requirement",
      flex: 1,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.4", padding: "8px 12px" },
    },
    {
      headerName: "AI Feedback",
      field: "ai_answer",
      flex: 1,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.4", padding: "8px 12px", color: "#6b7280" },
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8f9fa" }}>
        <SEO title="Analyzing Video..." />
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: PALETTE.inchworm }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>Analyzing your video...</h2>
          <p className="text-sm" style={{ color: "#6b7280" }}>This usually takes 1–3 minutes. You can also check the email we'll send you.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8f9fa" }}>
        <SEO title="Analysis Error" />
        <div className="text-center max-w-md px-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: "#fef2f2" }}>
            <X className="w-7 h-7" style={{ color: "#ef4444" }} strokeWidth={3} />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>Analysis Failed</h2>
          <p className="text-sm mb-6" style={{ color: "#6b7280" }}>{error}</p>
          <Button onClick={() => navigate("/")} className="rounded-full font-semibold" style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#f8f9fa" }}>
      <SEO title={`Free Video Check — ${score}%`} />

      {/* Header */}
      <div style={{ background: PALETTE.gunmetal, padding: "16px 24px" }} className="flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src="/brandcomply_logo.png" alt="BrandComply" className="h-7 w-7" />
          <span className="text-lg font-bold" style={{ color: PALETTE.brightSnow }}>BrandComply</span>
        </div>
        <Button onClick={() => navigate("/")} className="rounded-full font-semibold text-sm px-5" style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal, border: "none" }}>
          Sign Up Free <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Title */}
        <h1 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>Free Video Compliance Check</h1>
        <p className="text-sm mb-8" style={{ color: "#6b7280" }}>Analyzed against best-practice UGC campaign guidelines</p>

        {/* Score + Summary Row */}
        <div className="flex flex-wrap items-center gap-8 mb-8">
          {/* Score Circle */}
          <div className="flex flex-col items-center">
            <div
              style={{
                width: 110,
                height: 110,
                borderRadius: "50%",
                border: `6px solid ${scoreColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span className="text-4xl font-extrabold" style={{ color: PALETTE.gunmetal }}>{Math.round(score)}%</span>
            </div>
            <span className="text-xs mt-2" style={{ color: "#6b7280" }}>Compliance Score</span>
          </div>

          {/* Counts */}
          <div className="flex gap-4">
            <Card className="border-0 shadow-sm" style={{ background: "#f0fdf4" }}>
              <CardContent className="p-5 text-center">
                <div className="text-3xl font-bold" style={{ color: "#22c55e" }}>{(data.compliant || []).length}</div>
                <div className="text-xs mt-1" style={{ color: "#6b7280" }}>Compliant</div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm" style={{ background: "#fef2f2" }}>
              <CardContent className="p-5 text-center">
                <div className="text-3xl font-bold" style={{ color: "#ef4444" }}>{(data.non_compliant || []).length}</div>
                <div className="text-xs mt-1" style={{ color: "#6b7280" }}>Non-compliant</div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Requirements Grid */}
        <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: 12 }}>
          <CardContent className="p-0">
            <div className="ag-theme-quartz" style={{ width: "100%", height: Math.max(400, requirements.length * 52 + 56) }}>
              <AgGridReact
                ref={gridRef}
                rowData={requirements}
                columnDefs={columnDefs}
                pagination={false}
                rowHeight={40}
                headerHeight={44}
                domLayout="normal"
                suppressCellFocus
                tooltipShowDelay={200}
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-10 p-8 rounded-2xl text-center" style={{ background: PALETTE.gunmetal }}>
          <h2 className="text-2xl font-bold mb-3" style={{ color: PALETTE.brightSnow }}>
            Want to check videos against your own briefings?
          </h2>
          <p className="text-sm mb-6" style={{ color: "rgba(255,255,255,0.7)" }}>
            Create custom campaigns, invite creators, and review every submission — all for free.
          </p>
          <Button onClick={() => navigate("/")} className="rounded-full font-semibold text-base px-8 py-3 hover:scale-105 transition-transform" style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal, border: "none" }}>
            Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FreeCheckResult;
